import { List, OrderedMap } from '@gitbook/util-immutable';

import { isBlock, MODEL_TYPES } from '../constants';
import { generateKey } from '../utils';
import { Data } from './data';
import type { Fragment, FragmentMap } from './fragment';
import type { Inline } from './inline';
import { AbstractNode, memoizeMethods } from './node';
import { Text } from './text';

interface BlockProperties {
    key: string;
    isVoid: boolean;
    type: string;
    data: Data;
    fragments: FragmentMap;
    nodes: List<Block> | List<Inline | Text>;
}

// Argument to create a block
export type BlockCreateProps =
    | string
    | Block
    | Partial<
          Omit<BlockProperties, 'data' | 'nodes' | 'fragments'> & {
              data: Data | object;
              nodes: Block[] | List<Block> | (Text | Inline)[] | List<Text | Inline>;
              fragments: FragmentMap | Fragment[];
          }
      >;

/*
 * Model to represent a block node.
 */
export class Block extends AbstractNode {
    public declare readonly key: string;
    public declare readonly type: string;
    public declare readonly data: Data;
    public declare readonly isVoid: boolean;
    public declare readonly fragment: never;
    public declare readonly fragments: FragmentMap;
    public declare readonly nodes: List<Block> | List<Inline | Text>;

    get object(): 'block' {
        return 'block';
    }

    /**
     * Check if the block is empty.
     * Returns true if block is not void and all it's children nodes are empty.
     * Void node is never empty, regardless of it's content.
     */
    get isEmpty(): boolean {
        return !this.isVoid && !this.nodes.some((child) => !child.isEmpty);
    }

    /**
     * Get the concatenated text of all the block's children.
     */
    get text(): string {
        return this.getText();
    }

    /**
     * Return nodes as a list of blocks.
     */
    get blockNodes(): List<Block> {
        if (this.nodes.size === 0 || !Block.isBlock(this.nodes.get(0))) {
            // This isn't a block list so return an empty list
            return List.create();
        }
        return this.nodes as List<Block>;
    }

    /**
     * Create a new `Block` from `attrs`.
     */
    public static create(attrs: BlockCreateProps = {}): Block {
        if (Block.isBlock(attrs)) {
            return attrs;
        }

        if (typeof attrs === 'string') {
            return Block.createRecord({
                key: generateKey(),
                type: attrs,
            });
        }

        return Block.createRecord({
            type: attrs.type || '',
            key: attrs.key || generateKey(),
            data: Data.create(attrs.data || {}),
            isVoid: attrs.isVoid || false,
            fragments: OrderedMap.isOrderedMap(attrs.fragments)
                ? attrs.fragments
                : OrderedMap.create(
                      (attrs.fragments || []).map((fragment) => [fragment.fragmentKey, fragment])
                  ),
            nodes: List.create(attrs.nodes),
        });
    }

    /**
     * Create a new `Block` void with `attrs`.
     * It's already initialized with an empty text to avoid unecessary normalization.
     */
    public static createVoid(attrs: Omit<BlockCreateProps, 'isVoid'> = {}): Block {
        if (Block.isBlock(attrs)) {
            return attrs.merge({
                isVoid: true,
                nodes: List.create([Text.create()]),
            });
        }

        if (typeof attrs === 'string') {
            return Block.createRecord({
                key: generateKey(),
                type: attrs,
                isVoid: true,
                nodes: List.create([Text.create()]),
            });
        }

        return Block.create({
            isVoid: true,
            nodes: List.create([Text.create()]),
            ...attrs,
        });
    }

    /**
     * Check if `input` is a `Block`.
     */
    public static isBlock(input: unknown): input is Block {
        return isBlock(input);
    }

    /**
     * Check if `input` is a block list.
     */
    public static isBlockList(input: unknown): input is List<Block> {
        return List.isList(input) && input.every((item) => Block.isBlock(item));
    }
}

/*
 * Attach a pseudo-symbol for type checking.
 */
Block.prototype[MODEL_TYPES.BLOCK] = true;

memoizeMethods(Block);
